import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Phone, Mail, MessageCircle, ArrowUpRight, Check } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SERVICES = [
  "Social Media Management",
  "Reels & Creative Production",
  "Performance Marketing",
  "Website Design & Development",
  "Branding & Identity",
  "Copywriting & Content",
  "Marketing Consulting",
  "Other / Not sure",
];

const initial = { name: "", brand_company: "", email: "", phone: "", service_interested: "", message: "" };

export default function Contact() {
  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => { document.title = "Contact — The Indian Marketing"; }, []);

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill name, email and message.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, form);
      setSubmitted(true);
      toast.success("Thank you for contacting The Indian Marketing. Our team will get back to you shortly.");
      setForm(initial);
    } catch (err) {
      console.error(err);
      toast.error("Something went wrong. Please try again or WhatsApp us directly.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page" className="bg-[#2E2E31] text-white">
      <section className="pt-40 pb-16 md:pt-48 md:pb-20 relative overflow-hidden">
        <div className="absolute inset-0 grid-pattern opacity-30" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-10">
          <div className="flex items-center gap-3 mb-8">
            <span className="h-px w-10 bg-[#D4AF37]" />
            <span className="font-outfit text-xs uppercase tracking-[0.3em] text-[#D4AF37]">Begin a Conversation</span>
          </div>
          <h1 className="font-playfair text-5xl md:text-6xl lg:text-7xl text-white tracking-tight leading-[1.05] max-w-4xl">
            Let's Create Something <em className="text-[#D4AF37] font-normal">Meaningful.</em>
          </h1>
          <p className="font-outfit text-lg md:text-xl text-[#D9D9D9] mt-10 max-w-3xl leading-relaxed">
            Whether you need consistent content, better reels, a stronger website, performance campaigns or complete digital management — we'd love to understand your brand.
          </p>
        </div>
      </section>

      <section className="pb-28">
        <div className="max-w-7xl mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-16">
          {/* Contact info */}
          <motion.div
            initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }} transition={{ duration: 0.7 }}
            className="lg:col-span-5"
          >
            <div className="font-outfit text-xs uppercase tracking-[0.25em] text-[#D4AF37] mb-6">Direct Lines</div>
            <div className="space-y-8">
              <a href="mailto:kirti@ikoaedge.com" data-testid="contact-email" className="block group">
                <div className="font-outfit text-xs text-[#D9D9D9]/60 uppercase tracking-[0.2em] mb-2 flex items-center gap-2">
                  <Mail size={12} className="text-[#D4AF37]" /> Email
                </div>
                <div className="font-playfair text-2xl md:text-3xl text-white group-hover:text-[#D4AF37] transition-colors tracking-tight">
                  kirti@ikoaedge.com
                </div>
              </a>
              <a href="tel:+919326800270" data-testid="contact-phone" className="block group">
                <div className="font-outfit text-xs text-[#D9D9D9]/60 uppercase tracking-[0.2em] mb-2 flex items-center gap-2">
                  <Phone size={12} className="text-[#D4AF37]" /> Phone
                </div>
                <div className="font-playfair text-2xl md:text-3xl text-white group-hover:text-[#D4AF37] transition-colors tracking-tight">
                  +91 93268 00270
                </div>
              </a>
              <a href="https://wa.me/919326800270" target="_blank" rel="noreferrer" data-testid="contact-whatsapp" className="block group">
                <div className="font-outfit text-xs text-[#D9D9D9]/60 uppercase tracking-[0.2em] mb-2 flex items-center gap-2">
                  <MessageCircle size={12} className="text-[#D4AF37]" /> WhatsApp
                </div>
                <div className="font-playfair text-2xl md:text-3xl text-white group-hover:text-[#D4AF37] transition-colors tracking-tight">
                  +91 93268 00270
                </div>
              </a>
            </div>

            <div className="mt-14 p-8 border border-[#D4AF37]/30 bg-[#1c1c1f]">
              <div className="font-outfit text-xs uppercase tracking-[0.25em] text-[#D4AF37] mb-4">Engagement Window</div>
              <p className="font-playfair text-2xl text-white leading-snug">
                We open a limited number of new engagements every quarter.
              </p>
              <p className="font-outfit text-sm text-[#D9D9D9] mt-4 leading-relaxed">
                If our boutique model resonates with your ambition, share a few details and our team will respond within one business day.
              </p>
            </div>
          </motion.div>

          {/* Form */}
          <motion.div
            initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }} transition={{ duration: 0.7, delay: 0.15 }}
            className="lg:col-span-7"
          >
            {submitted ? (
              <div
                data-testid="contact-success"
                className="border border-[#D4AF37]/40 p-12 md:p-16 bg-[#1c1c1f] flex flex-col items-start"
              >
                <div className="w-16 h-16 rounded-full border-2 border-[#D4AF37] flex items-center justify-center mb-8">
                  <Check size={32} className="text-[#D4AF37]" />
                </div>
                <h2 className="font-playfair text-3xl md:text-4xl text-white tracking-tight leading-tight">
                  Thank you for contacting The Indian Marketing.
                </h2>
                <p className="font-outfit text-base text-[#D9D9D9] mt-4 max-w-lg leading-relaxed">
                  Our team will get back to you shortly. For urgent enquiries, you can also reach us on WhatsApp at +91 93268 00270.
                </p>
                <button
                  onClick={() => setSubmitted(false)}
                  data-testid="contact-new-message"
                  className="mt-10 inline-flex items-center gap-2 text-[#D4AF37] font-outfit text-xs uppercase tracking-[0.2em] hover:gap-3 transition-all"
                >
                  Send Another Message <ArrowUpRight size={14} />
                </button>
              </div>
            ) : (
              <form onSubmit={onSubmit} data-testid="contact-form" className="space-y-10">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
                  <Field label="Name" name="name" value={form.name} onChange={onChange} required testid="contact-input-name" />
                  <Field label="Brand / Company" name="brand_company" value={form.brand_company} onChange={onChange} testid="contact-input-brand" />
                  <Field label="Email" name="email" type="email" value={form.email} onChange={onChange} required testid="contact-input-email" />
                  <Field label="Phone" name="phone" value={form.phone} onChange={onChange} testid="contact-input-phone" />
                </div>

                <div>
                  <label className="block font-outfit text-xs text-[#D9D9D9]/70 uppercase tracking-[0.25em] mb-3">
                    Service Interested In
                  </label>
                  <select
                    name="service_interested"
                    value={form.service_interested}
                    onChange={onChange}
                    data-testid="contact-input-service"
                    className="w-full bg-transparent border-b border-white/20 focus:border-[#D4AF37] outline-none text-white py-3 px-0 font-outfit text-base transition-colors appearance-none cursor-pointer"
                  >
                    <option value="" className="bg-[#2E2E31] text-white">Select a service</option>
                    {SERVICES.map((s) => (
                      <option key={s} value={s} className="bg-[#2E2E31] text-white">{s}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block font-outfit text-xs text-[#D9D9D9]/70 uppercase tracking-[0.25em] mb-3">
                    Message <span className="text-[#D4AF37]">*</span>
                  </label>
                  <textarea
                    name="message"
                    value={form.message}
                    onChange={onChange}
                    rows={6}
                    required
                    data-testid="contact-input-message"
                    placeholder="Tell us about your brand, growth stage and what you'd like to explore."
                    className="w-full bg-transparent border-b border-white/20 focus:border-[#D4AF37] outline-none text-white py-3 px-0 font-outfit text-base placeholder-[#D9D9D9]/30 transition-colors resize-none"
                  />
                </div>

                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="contact-submit"
                  className="inline-flex items-center gap-3 bg-[#D4AF37] text-black font-outfit font-medium px-10 py-5 uppercase tracking-[0.2em] text-sm hover:bg-[#F0C84B] transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {submitting ? "Sending..." : "Submit Enquiry"} <ArrowUpRight size={16} />
                </button>
              </form>
            )}
          </motion.div>
        </div>
      </section>
    </div>
  );
}

function Field({ label, name, value, onChange, type = "text", required, testid }) {
  return (
    <div>
      <label className="block font-outfit text-xs text-[#D9D9D9]/70 uppercase tracking-[0.25em] mb-3">
        {label} {required && <span className="text-[#D4AF37]">*</span>}
      </label>
      <input
        type={type}
        name={name}
        value={value}
        onChange={onChange}
        required={required}
        data-testid={testid}
        className="w-full bg-transparent border-b border-white/20 focus:border-[#D4AF37] outline-none text-white py-3 px-0 font-outfit text-base transition-colors"
      />
    </div>
  );
}
